import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    createContext,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../context';
import {
    List,
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
} from 'react-virtualized';
import hasPermission from '../../Shared/hasPermission';
import PracticeListItem from './PracticeListItem/PracticeListItem';
import {
    getStatusBackgroundClass,
    getStatusBadgeText,
} from './PracticeListStatusHelper';
 
export const PracticeListPermissionsContext = createContext();
 
const loadingStyle = {
    position: 'absolute',
    bottom: -25,
    left: 0,
    right: 0,
    padding: '6px',
    textAlign: 'center',
    fontSize: '14px',
    color: 'white'
}
 
function PracticesList() {
    const dispatch = useDispatch();
 
    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
 
    // TODO: this is part of the data sync, so when that is refactored refactor this as well
    const {
        assignedFilterUser,
        bookmarked,
        includeTestPractices,
        includeDormantPractices,
        onlyShowAssigned,
        practiceFilter,
        practiceTab,
        searchCustomIds,
        searchGroups,
        searchNotes,
        searchPartnerOfferings,
        searchPracticePims,
    } = useSelector(
        (state) => ({
            assignedFilterUser: state.practice.assignedFilterUser, // null or a guid to filter practice list by the given userId
            bookmarked: state.practice.bookmarked, // bool to filter for bookmarked practices
            includeTestPractices: state.practice.includeTestPractices, // bool tied to the view test practices toggle
            includeDormantPractices: state.practice.includeDormantPractices, // bool tied to the view dormant practices toggle
            onlyShowAssigned: state.practice.onlyShowAssigned, // bool tied to the filter assigned practices button
            practiceFilter: state.practice.filter, // string (defualt empty) that filters practice list based on the input
            practiceTab: state.practice.practiceLink, // string (default is 'all') tied to the practices filter selector
            searchCustomIds: state.practice.searchCustomIds, // bool tied to Search Partner Ids toggle in the text search section
            searchGroups: state.practice.searchGroups, // bool tied to the Serch Practice Groups toggle in the text search section
            searchNotes: state.practice.searchNotes, // bool tied to the Search Practice Notes toggle in the text search section
            searchPartnerOfferings: state.practice.searchPartnerOfferings, // bool tied to the Search Partner Offerings toggle in the text search section
            searchPracticePims: state.practice.searchPracticePims, // bool tied to the Search Practice Pims toggle in the text search section,
        }),
        shallowEqual
    );
    // TODO: improve the below behavior when doing the data sync optimization
    const practiceListRef = useRef(null);
    const [practices, setPractices] = useState([]);
    const skip = useRef(null);
    const hasMore = useRef(true);
    const shouldResetQuery = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
 
    const can = useCallback(
        (permission) => hasPermission(permission, permissions),
        [permissions]
    );
 
    const canDo = useMemo(
        () => ({
            canSearchGroups: can('SearchGroups'),
            canSeePracticeListStatusIcons: can('PracticeListStatusIcons'),
            canAssignUsers: can('Assignment'),
            canMutePractices: can('Muting'),
        }),
        [can]
    );
 
    const params = useMemo(() => {
        shouldResetQuery.current = true;
        skip.current = null
        hasMore.current = true
        return {
            practiceTab,
            filter: practiceFilter,
            onlyShowBookmarked: bookmarked,
            onlyShowAssigned,
            assignedFilterUser,
            includeTestPractices,
            includeDormantPractices,
            searchPracticeNotes: searchNotes,
            searchPracticeGroups: searchGroups,
            searchCustomIds,
            searchPartnerOfferings,
            searchPracticePims
        }
    }, [practiceTab, practiceFilter, bookmarked, onlyShowAssigned,
        assignedFilterUser, includeTestPractices, includeDormantPractices,
        searchNotes, searchGroups, searchCustomIds, searchPartnerOfferings,
        searchPracticePims
    ]);
 
 
    const fetchPractices = useCallback(() => {
        if (isLoading || !hasMore.current) return;
        
        setIsLoading(true)
        dispatch(actions.practice.getPractices({...params, skip: skip.current})).then((res) => {
            const newPracticeList = res?.data || [];
            
            if(res?.status === 200) {
                if(newPracticeList.length === 0) {
                    hasMore.current = false;
                    if(skip.current === null) setPractices([]);
                } else {
                    if(shouldResetQuery.current) {
                        setPractices(newPracticeList);
                        shouldResetQuery.current = false;
                    } else {
                        setPractices((prev) => [...prev, ...newPracticeList]);
                    }
                    skip.current = (skip.current ?? 0) + 100;
                }
            }
            setIsLoading(false)
        });
    }, [dispatch, params, isLoading]);
 
    // TODO: if a component dispatches show only assigned and assigned user should be tied and batched together somehow
    useEffect(() => {
        if(skip.current !== null) return;
        fetchPractices()
    }, [fetchPractices]);
 
    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 100,
        })
    );
 
    const handleScroll = useCallback(({ clientHeight, scrollHeight, scrollTop }) => {
        const threshold = 10;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
 
        if (isAtBottom && practiceListRef.current) {
            fetchPractices()
        }
    }, [fetchPractices]);
 
    const renderPractice = (p) => {
        const statusContent = {
            statusBackgroundClass: getStatusBackgroundClass(p),
            statusBadgeText: getStatusBadgeText(p),
        };
 
        p.practiceMute = p.practiceMute || { muteUntil: 0, muteNote: null };
 
        const itemProps = {
            ...statusContent,
            practice: p,
        };
 
        return (
            <PracticeListItem
                key={p.id}
                filter={practiceFilter}
                searchCustomIds={searchCustomIds}
                searchPartnerOfferings={searchPartnerOfferings}
                searchPracticePims={searchPracticePims}
                {...itemProps}
            />
        );
    };
 
    return (
        <PracticeListPermissionsContext.Provider value={canDo}>
            <div style={{ position: 'relative', height: '100%' }}>
                <AutoSizer>
                    {({ width, height }) => (
                        <List
                            ref={practiceListRef}
                            width={width}
                            height={height}
                            rowHeight={cache.current.rowHeight}
                            deferredMeasurementCache={cache.current}
                            rowCount={practices.length}
                            onScroll={handleScroll}
                            rowRenderer={({ key, index, style, parent }) => {
                                const practice = practices[index];
 
                                return (
                                    <CellMeasurer
                                        key={key}
                                        cache={cache.current}
                                        parent={parent}
                                        columnIndex={0}
                                        rowIndex={index}>
                                        <div style={style} className="mx-2">
                                            {renderPractice(practice)}
                                        </div>
                                    </CellMeasurer>
                                );
                            }}
                        />
                    )}
                </AutoSizer>
                
                {isLoading && skip.current !== null && (
                    <div style={loadingStyle} >
                        <div className="spinner-border text-primary" role="status"/>
                    </div>
                )}
                
                {!hasMore.current && practices.length > 0 && (
                    <div style={loadingStyle}>
                        No more data available
                    </div>
                )}
            </div>
        </PracticeListPermissionsContext.Provider>
    );
}
 
export default React.memo(PracticesList);